import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, ScrollText, AlertTriangle, TrendingUp, CalendarCheck, Gavel, Star, HeartPulse, MessageSquareWarning, FileSignature, MailWarning, GraduationCap, ShieldAlert, Clock, Cake, PartyPopper, Wallet } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { ADMIN_DASHBOARD } from "@/constants/testIds";
import { PendingConfirmationsPanel } from "@/components/PendingConfirmationsPanel";

const STATUS_MAP = {
  verde: { color: "bg-emerald-500", label: "En orden", text: "text-emerald-700", bg: "bg-emerald-50" },
  amarillo: { color: "bg-amber-500", label: "Atención requerida", text: "text-amber-700", bg: "bg-amber-50" },
  rojo: { color: "bg-red-500", label: "Riesgo alto", text: "text-red-700", bg: "bg-red-50" },
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [billing, setBilling] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setSummary(data));
    api.get("/billing/status").then(({ data }) => setBilling(data));
    api.get("/dashboard/attendance-today").then(({ data }) => setAttendance(data));
  }, []);

  const status = summary ? STATUS_MAP[summary.status] : null;

  const goToHighRisk = () => {
    const ids = summary?.high_risk_employee_ids || [];
    if (ids.length === 1) navigate(`/admin/empleados/${ids[0]}`);
    else navigate("/admin/empleados?filter=high_risk");
  };

  const teaserModules = [
    { icon: CalendarCheck, title: "Control de Vacaciones", desc: "Art. 76/78 LFT · aprobación y saldos", to: "/admin/vacaciones", ready: true },
    { icon: Gavel, title: "Aviso de Rescisión Asistido", desc: "Art. 47 LFT · pestaña Baja en cada expediente", to: "/admin/empleados?tab=baja", ready: true },
    { icon: TrendingUp, title: "Considera Antes de Decidir", desc: "Estimado de costo y guía de conversación · pestaña Considera Antes de Decidir", to: "/admin/empleados?tab=checkpoint", ready: true },
    { icon: Star, title: "Mapa de Talento Crítico", desc: "Antigüedad e incidencias · quién conviene retener", to: "/admin/talento", ready: true },
  ];

  return (
    <AdminLayout>
      <div className="max-w-5xl">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Dashboard de Cumplimiento</h1>
        <p className="text-charcoal/60 mt-1">Panorama general, no es una lista de documentos.</p>

        {(billing?.billing_status === "grace_period" || billing?.billing_status === "read_only" || billing?.billing_status === "canceled") && (
          <button
            onClick={() => navigate("/admin/facturacion")}
            className={`mt-6 w-full text-left rounded-xl p-5 flex items-center gap-3 border hover:-translate-y-0.5 hover:shadow-lg transition-all ${
              billing.billing_status === "grace_period" ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-300"
            }`}
          >
            <ShieldAlert className={`w-5 h-5 shrink-0 ${billing.billing_status === "grace_period" ? "text-amber-600" : "text-red-600"}`} strokeWidth={1.5} />
            <p className={`text-sm ${billing.billing_status === "grace_period" ? "text-amber-800" : "text-red-800"}`}>
              {billing.billing_status === "grace_period"
                ? "Tuvimos un problema al cobrar tu suscripción. Actualiza tu método de pago en Facturación antes de pasar a solo lectura."
                : "Tu cuenta está en modo solo lectura por un pago pendiente. Actualiza tu método de pago en Facturación para recuperar el acceso completo."}
            </p>
          </button>
        )}

        {summary && (
          <div data-testid={ADMIN_DASHBOARD.semaphore} className={`mt-8 rounded-2xl p-6 flex items-center gap-4 ${status.bg} border border-petroleum/10`}>
            <div className={`w-4 h-4 rounded-full ${status.color} animate-pulse`} />
            <div>
              <p className={`font-heading font-bold text-lg ${status.text}`}>{status.label}</p>
              <p className="text-sm text-charcoal/60">Basado en políticas pendientes, incidencias recientes y solicitudes de vacaciones sin resolver.</p>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
          <StatCard testid={ADMIN_DASHBOARD.statTotalEmployees} icon={Users} label="Empleados" value={summary?.total_employees} onClick={() => navigate("/admin/empleados")} />
          <StatCard testid={ADMIN_DASHBOARD.statPendingPolicies} icon={ScrollText} label="Políticas pendientes de acuse" value={summary?.pending_policy_acknowledgements} accent onClick={() => navigate("/admin/empleados?filter=pending_policies")} />
          <StatCard testid={ADMIN_DASHBOARD.statIncidents} icon={AlertTriangle} label="Incidencias registradas" value={summary?.total_incidents} onClick={() => navigate("/admin/empleados?filter=has_incidents")} />
          <StatCard testid={ADMIN_DASHBOARD.statPendingVacations} icon={CalendarCheck} label="Vacaciones por aprobar" value={summary?.pending_vacation_requests} accent onClick={() => navigate("/admin/vacaciones")} />
          <StatCard
            testid={ADMIN_DASHBOARD.statTrainingInvestment}
            icon={GraduationCap}
            label={`Invertido en capacitación (${summary?.training_year ?? ""})`}
            value={summary ? `$${summary.training_total_cost_this_year.toLocaleString("es-MX")}` : undefined}
            onClick={() => navigate("/admin/capacitacion")}
          />
          <StatCard
            testid={ADMIN_DASHBOARD.statTotalPayroll}
            icon={Wallet}
            label="Total de nómina mensual"
            value={summary ? `$${summary.total_payroll.toLocaleString("es-MX")}` : undefined}
            onClick={() => navigate("/admin/nomina")}
          />
          <AttendanceQuadrantCard attendance={attendance} onClick={() => navigate("/admin/asistencia-hoy")} />
        </div>

        {summary?.employees_missing_punch > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.missingPunchBanner}
            onClick={() => navigate("/admin/jornada")}
            className="mt-6 w-full text-left bg-card border border-terracotta/30 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <Clock className="w-5 h-5 text-terracotta shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_missing_punch}</b> empleado(s) no han registrado su entrada hoy (pasadas las {summary.expected_checkin_time}
              {summary.expected_checkin_tolerance_minutes > 0 ? ` + ${summary.expected_checkin_tolerance_minutes} min de tolerancia` : ""}). Revisa el Registro de Jornada.
            </p>
          </button>
        )}

        {summary?.employees_early_checkout > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.earlyCheckoutBanner}
            onClick={() => navigate("/admin/jornada")}
            className="mt-6 w-full text-left bg-card border border-terracotta/30 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <Clock className="w-5 h-5 text-terracotta shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_early_checkout}</b> empleado(s) salieron antes de lo esperado o no han marcado su salida hoy (esperada a las {summary.expected_checkout_time}
              {summary.expected_checkin_tolerance_minutes > 0 ? ` ± ${summary.expected_checkin_tolerance_minutes} min de tolerancia` : ""}). Revisa el Registro de Jornada.
            </p>
          </button>
        )}

        {summary?.employees_high_incidents > 0 && (
          <button data-testid={ADMIN_DASHBOARD.statHighRisk} onClick={goToHighRisk} className="mt-6 w-full text-left bg-card border border-terracotta/30 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all">
            <AlertTriangle className="w-5 h-5 text-terracotta shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_high_incidents}</b> empleado(s) con 3 o más incidencias — candidatos a revisar en Considera Antes de Decidir.
            </p>
          </button>
        )}

        {summary?.employees_unscheduled_vacation > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.unscheduledVacationBanner}
            onClick={() => navigate("/admin/empleados?filter=unscheduled_vacation")}
            className="mt-4 w-full text-left bg-card border border-petroleum/20 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <CalendarCheck className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_unscheduled_vacation}</b> empleado(s) con días de vacaciones acumulados de años anteriores sin programar tomarlos (Art. 76/78 LFT).
            </p>
          </button>
        )}

        {summary?.employees_missing_emergency_contact > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.missingEmergencyContactBanner}
            onClick={() => navigate("/admin/empleados?filter=missing_emergency_contact")}
            className="mt-4 w-full text-left bg-card border border-petroleum/20 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <HeartPulse className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_missing_emergency_contact}</b> empleado(s) sin contacto de emergencia capturado en su expediente.
            </p>
          </button>
        )}

        {summary?.pending_data_correction_requests > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.pendingCorrectionsBanner}
            onClick={() => navigate("/admin/correcciones")}
            className="mt-4 w-full text-left bg-card border border-terracotta/30 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <MessageSquareWarning className="w-5 h-5 text-terracotta shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.pending_data_correction_requests}</b> solicitud(es) de corrección de datos pendiente(s) de revisión.
            </p>
          </button>
        )}

        {summary?.signable_documents_needing_reminder > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.signatureReminderBanner}
            onClick={() => navigate("/admin/politicas")}
            className="mt-4 w-full text-left bg-card border border-amber-300 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <FileSignature className="w-5 h-5 text-amber-600 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.signable_documents_needing_reminder}</b> documento(s) llevan 3 días o más pendientes de firma física. Da seguimiento desde Biblioteca de Documentos.
            </p>
          </button>
        )}

        {summary?.failed_email_count > 0 && (
          <button
            data-testid={ADMIN_DASHBOARD.failedEmailsBanner}
            onClick={() => navigate("/admin/envios-fallidos")}
            className="mt-4 w-full text-left bg-card border border-red-300 rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            <MailWarning className="w-5 h-5 text-red-600 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.failed_email_count}</b> correo(s) no se pudieron enviar (verificación, bienvenida, avisos). Revisa Envíos fallidos.
            </p>
          </button>
        )}

        {summary?.employees_upcoming_birthday > 0 && (
          <div
            data-testid={ADMIN_DASHBOARD.upcomingBirthdaysBanner}
            className="mt-4 w-full bg-card border border-petroleum/20 rounded-xl p-5 flex items-center gap-3"
          >
            <Cake className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_upcoming_birthday}</b> empleado(s) cumplen años en los próximos 7 días:{" "}
              {summary.employees_upcoming_birthday_list.map((e, i) => (
                <span key={e.id}>
                  {i > 0 && ", "}
                  {e.full_name} ({e.days_until === 0 ? "hoy" : `en ${e.days_until} día(s)`})
                </span>
              ))}
              .
            </p>
          </div>
        )}

        {summary?.employees_upcoming_anniversary > 0 && (
          <div
            data-testid={ADMIN_DASHBOARD.upcomingAnniversariesBanner}
            className="mt-4 w-full bg-card border border-petroleum/20 rounded-xl p-5 flex items-center gap-3"
          >
            <PartyPopper className="w-5 h-5 text-petroleum shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-charcoal/80">
              <b>{summary.employees_upcoming_anniversary}</b> empleado(s) cumplen aniversario laboral en los próximos 7 días:{" "}
              {summary.employees_upcoming_anniversary_list.map((e, i) => (
                <span key={e.id}>
                  {i > 0 && ", "}
                  {e.full_name} ({e.years} año(s), {e.days_until === 0 ? "hoy" : `en ${e.days_until} día(s)`})
                </span>
              ))}
              .
            </p>
          </div>
        )}

        {summary?.salario_minimo_outdated && (
          <div
            data-testid={ADMIN_DASHBOARD.salarioMinimoBanner}
            className="mt-4 w-full bg-amber-50 border border-amber-300 rounded-xl p-5 flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-amber-800">
              <b>Reforma salarial:</b> {summary.salario_minimo_notice}
            </p>
          </div>
        )}

        <PendingConfirmationsPanel />

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Accesos rápidos</h2>        <div className="grid sm:grid-cols-2 gap-4">
          {teaserModules.map((m, i) => {
            const Card = (
              <div key={i} data-testid={ADMIN_DASHBOARD.moduleTeaserCard} className={`bg-card border border-petroleum/10 rounded-xl p-5 ${m.ready ? "hover:-translate-y-0.5 hover:shadow-lg transition-all cursor-pointer" : "opacity-70"}`}>
                <m.icon className="w-5 h-5 text-petroleum mb-2" strokeWidth={1.5} />
                <p className="font-semibold text-charcoal text-sm">{m.title}</p>
                <p className="text-xs text-charcoal/50 mt-1">{m.desc}</p>
              </div>
            );
            return m.ready ? (
              <div key={i} role="button" onClick={() => navigate(m.to)}>{Card}</div>
            ) : Card;
          })}
        </div>
      </div>
    </AdminLayout>
  );
}

function AttendanceQuadrantCard({ attendance, onClick }) {
  const needsConfig = attendance && !attendance.expected_checkin_time;
  return (
    <button
      data-testid={ADMIN_DASHBOARD.attendanceCard}
      onClick={onClick}
      className="text-left bg-card border border-petroleum/10 rounded-xl p-6 shadow-[0_8px_30px_rgba(14,79,79,0.06)] hover:-translate-y-0.5 hover:shadow-lg transition-all"
    >
      <div className="flex items-center gap-2 mb-3">
        <Clock className="w-5 h-5 text-petroleum" strokeWidth={1.5} />
        <p className="text-sm text-charcoal/60">Puntualidad de hoy</p>
      </div>
      {needsConfig ? (
        <p className="text-xs text-charcoal/50">Configura la hora de entrada esperada en Perfil de Empresa para ver este conteo.</p>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          <div>
            <p className="text-xl font-heading font-bold text-emerald-600">{attendance?.on_time ?? "–"}</p>
            <p className="text-xs text-charcoal/50">A tiempo</p>
          </div>
          <div>
            <p className="text-xl font-heading font-bold text-amber-600">{attendance?.late ?? "–"}</p>
            <p className="text-xs text-charcoal/50">Retardo</p>
          </div>
          <div>
            <p className="text-xl font-heading font-bold text-red-600">{attendance?.absent ?? "–"}</p>
            <p className="text-xs text-charcoal/50">Ausencia</p>
          </div>
          <div>
            <p className="text-xl font-heading font-bold text-charcoal">{attendance?.checked_out ?? "–"}</p>
            <p className="text-xs text-charcoal/50">Salida</p>
          </div>
        </div>
      )}
    </button>
  );
}

function StatCard({ testid, icon: Icon, label, value, accent, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick} className="text-left bg-card border border-petroleum/10 rounded-xl p-6 shadow-[0_8px_30px_rgba(14,79,79,0.06)] hover:-translate-y-0.5 hover:shadow-lg transition-all">
      <Icon className={`w-5 h-5 mb-3 ${accent ? "text-terracotta" : "text-petroleum"}`} strokeWidth={1.5} />
      <p className="text-3xl font-heading font-bold text-charcoal">{value ?? "–"}</p>
      <p className="text-sm text-charcoal/60 mt-1">{label}</p>
    </button>
  );
}
